import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Search, Mic, X as ClearIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import useDebounce from '../../../../hooks/useDebounce';
import { RootState } from '../../../../reducers';
import { screenAppAPIClient } from '../../../../lib/baseAxios';
import { FileType } from '../../../../types';

interface MentionSelectorProps {
  isOpen: boolean;
  query: string;
  onSelect: (file: FileType) => void;
  onClose: () => void;
  selectedFiles: string[];
}

interface SearchResult extends FileType {
  fileId?: string;
  fileName?: string;
  recordingId?: string;
}

const MentionSelector: React.FC<MentionSelectorProps> = ({
  isOpen,
  query,
  onSelect,
  onClose,
  selectedFiles,
}) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const teamState = useSelector((state: RootState) => state.team.selectedTeam);
  const teamId = teamState?.teamId;

  // Sync external query to internal search
  useEffect(() => {
    if (isOpen && query) {
      setSearchQuery(query);
    }
  }, [isOpen, query]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Helper function to render file thumbnail
  const renderThumbnail = (file: SearchResult, fileName: string) => {
    if (file.streams && !file.streams.includes('video')) {
      return (
        <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0 flex items-center justify-center">
          <Mic className="w-4 h-4 text-gray-400 dark:text-gray-600 opacity-50" />
        </div>
      );
    }

    if (file.thumbUrl) {
      return (
        <img
          src={file.thumbUrl}
          alt={fileName}
          className="w-12 h-8 object-cover rounded flex-shrink-0 bg-gray-100 dark:bg-gray-800"
        />
      );
    }

    return (
      <div className="w-12 h-8 bg-gray-100 dark:bg-gray-800 rounded flex-shrink-0" />
    );
  };

  const fetchFiles = useCallback(
    async (search: string) => {
      if (!teamId) return;

      try {
        setLoading(true);
        setError(null);

        // Use same API as SearchDialog
        const response = await (screenAppAPIClient as any).api.get(
          `/v2/files`,
          {
            params: {
              teamId,
              searchQuery: search || undefined,
              sortBy: 'createdAt',
              sortOrder: 'desc',
              page: 1,
              limit: 10,
            },
          },
        );

        if (response.data.success && response.data.data) {
          setFiles(response.data.data.fileSystem || []);
        } else {
          setError('Failed to load files');
        }
      } catch (err: any) {
        console.error('Error fetching files:', err);
        setError('Failed to load files');
      } finally {
        setLoading(false);
      }
    },
    [teamId],
  );

  // Initial fetch and refetch on search change
  useEffect(() => {
    if (isOpen && teamId) {
      fetchFiles(debouncedSearchQuery);
      setSelectedIndex(0);
    }
  }, [isOpen, debouncedSearchQuery, teamId, fetchFiles]);

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, files.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const selectedFile = files[selectedIndex];
        if (selectedFile) {
          onSelect(selectedFile);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, files, selectedIndex, onSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (scrollAreaRef.current && files.length > 0) {
      const selectedElement = scrollAreaRef.current.querySelector(
        `[data-index="${selectedIndex}"]`,
      );
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, files.length]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed z-[9999] w-96 max-h-96 overflow-hidden bg-popover border border-border rounded-md shadow-md"
      style={{
        bottom: 80,
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: 'calc(100vw - 32px)',
      }}
    >
      {/* Search bar - matches FileSelector */}
      <div className="flex items-center gap-3 px-3 py-3 border-b border-gray-200 dark:border-gray-700">
        <Search className="h-4 w-4 text-gray-400" />
        <input
          ref={inputRef}
          type="text"
          placeholder={t(
            'drawer.multiAskAI.searchRecordings',
            'Search recordings...',
          )}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 bg-transparent outline-none text-base"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="opacity-70 transition-opacity hover:opacity-100"
          >
            <ClearIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* File list */}
      <ScrollArea className="h-64" ref={scrollAreaRef}>
        {(() => {
          if (loading) {
            return (
              <div>
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="flex gap-3 px-3 py-2.5 mb-0.5">
                    <Skeleton className="w-12 h-8 rounded flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-full max-w-[70%]" />
                    </div>
                  </div>
                ))}
              </div>
            );
          }

          if (error) {
            return (
              <div className="flex items-center justify-center h-full p-8">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            );
          }

          if (files.length === 0) {
            return (
              <div className="flex items-center justify-center h-full p-8">
                <p className="text-sm text-muted-foreground">
                  {searchQuery
                    ? t('chat.mention.noResults', 'No recordings found')
                    : t('chat.mention.noFiles', 'No recordings available')}
                </p>
              </div>
            );
          }

          return (
            <div>
              {files.map((file, index) => {
                const fileId = file.fileId || file._id;
                const fileName = file.fileName || file.name || 'Untitled';
                const isSelected = selectedFiles.includes(fileId);
                const isHighlighted = index === selectedIndex;

                return (
                  <button
                    key={fileId}
                    data-index={index}
                    onClick={() => onSelect(file)}
                    type="button"
                    className={cn(
                      'w-full text-left group flex items-center gap-3 px-3 py-2.5 mb-0.5 cursor-pointer transition-colors relative',
                      isHighlighted
                        ? 'bg-gray-100 dark:bg-gray-800'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800',
                    )}
                  >
                    {/* Thumbnail */}
                    <div className="relative">
                      {renderThumbnail(file, fileName)}
                      {/* Checkbox overlay on hover */}
                      <div
                        className={cn(
                          'absolute inset-0 flex items-center justify-center bg-black/60 rounded-md transition-opacity',
                          isSelected
                            ? 'opacity-100'
                            : 'opacity-0 group-hover:opacity-100',
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => onSelect(file)}
                          onClick={(e) => e.stopPropagation()}
                          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary border-white"
                        />
                      </div>
                    </div>

                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">
                        {fileName}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })()}
      </ScrollArea>
    </div>
  );
};

export default MentionSelector;
